<template>
  <create-account-layout
    :current-step="2"
    title="Verification"
  >
    <form
      novalidate="novalidate"
      @submit.prevent="handleSubmit"
    >
      <section>
        <p class="mb-6 mt-8 text-left text-sm text-dark-gray">
          {{ subtitle }}
        </p>
        <form-field
          label="Verification Code"
          :value="phone_twilio_validation"
          placeholder="Enter Verification Code"
          :on-input="onInput"
          name="phone_twilio_validation"
          maxlength="6"
          :is-valid="!error"
          error-message="Incorrect verification code"
        />
        <div class="flex flex-col">
          <button-main
            class="my-4 mb-4"
            :disabled="!isValidCode"
            type="submit"
            :loading-spinner="isLoading"
            value="Continue"
          >
            Continue
          </button-main>
          <div class="mt-4 flex flex-col items-center">
            <p class="mb-2 text-1md text-body-color">Code didn't send?</p>
            <router-link
              class="mx-auto w-max cursor-pointer text-1md text-yellow-500"
              to="/sign-up/create-account"
            >
              Try again
            </router-link>
          </div>
        </div>
      </section>
    </form>
  </create-account-layout>
</template>

<script>
import { mapState } from 'vuex';
import CreateAccountLayout from '@/components/layouts/CreateAccountLayout.vue';
import FormField from '@/components/FormField.vue';
import ButtonMain from '@/components/Button.vue';
import { toastResponse } from '@/common/handleServerResponse';

export default {
  name: 'SignUpOrLoginVerification',
  components: {
    CreateAccountLayout,
    FormField,
    ButtonMain,
  },
  data() {
    return {
      isLoading: false,
      error: false,
      phone_twilio_validation: '',
      displayPrivacyPolicyStep: false,
    };
  },
  computed: {
    isIdentityVerified() {
      return this.$store.state.users.currentUser?.last_verified_identity_at;
    },
    shouldShowPrivacyPolicy() {
      return (
        this.isPrivacyPolicyStepEnabled &&
        !this.isPrivacyPolicyConsentedAt &&
        !this.displayPrivacyPolicyStep
      );
    },
    inquiries() {
      return this.$store.state.users.inquiries.length > 0;
    },
    isValidCode() {
      return (
        this.phone_twilio_validation &&
        this.phone_twilio_validation.length === 6
      );
    },
    isIDVOnboardingFlowActive() {
      const status =
        this.$store.state.users.currentUser?.identity_proofing_status;
      if (status === 'skip_identity_proofing') return false;
      if (status === 'require_identity_proofing') return true;
      const featureFlagEnabled = this.$store.getters['users/getFeatureFlag']?.(
        'idv_onboarding_flow'
      )?.enabled;
      return !!featureFlagEnabled;
    },
    isPrivacyPolicyStepEnabled() {
      return this.$store.getters['users/getFeatureFlag']?.(
        'privacy_policy_step_enabled'
      )?.['enabled'];
    },
    isPrivacyPolicyConsentedAt() {
      return (
        this.$store.state.users.currentUser?.privacy_policy_consented_at
          ?.length > 0 || false
      );
    },
    isPhoneRequire() {
      return !this.isEmailRequiredForLogin && !this.userPhoneNumber;
    },
    isEmailRequire() {
      return this.isEmailRequiredForLogin && !this.userEmailAddress;
    },
    ...mapState({
      isEmailRequiredForLogin: (state) =>
        state.carriers?.isEmailRequiredForLogin || false,
      userPhoneNumber: (state) => state.signUpData?.phone_number || null,
      userEmailAddress: (state) => state.signUpData?.email_address || null,
    }),
    subtitle() {
      let source = 'mobile number';
      if (this.isEmailRequiredForLogin) source = 'email address';
      return `Enter the code we just sent to the ${source} you entered.`;
    },
  },
  created() {
    if (this.isPhoneRequire || this.isEmailRequire) {
      /*
       * Do not allow the user to enter the code without first registering
       * a cell phone or an email address.
       * Warning: should happen only when the page is refreshed
       */
      if (this.shouldShowPrivacyPolicy) return;
      this.$router.push({
        name: 'CreateAccountOrLogin',
      });
    }
  },
  methods: {
    getNextRoute(fallbackRoute) {
      let latestInquiryStatus = this.$store.getters['users/inquiryStatus'];
      let route = { name: fallbackRoute, query: {} };

      if (this.shouldShowPrivacyPolicy) {
        route.name = 'PrivacyPolicy';
        route.query = {
          redirectTo:
            this.isIDVOnboardingFlowActive &&
            latestInquiryStatus !== 'approved' &&
            latestInquiryStatus !== 'declined'
              ? 'IdentityVerification'
              : fallbackRoute,
        };
      } else if (
        this.isIDVOnboardingFlowActive &&
        latestInquiryStatus !== 'approved' &&
        latestInquiryStatus !== 'declined' &&
        !this.isIdentityVerified
      ) {
        route.name = 'IdentityVerification';
      }
      console.log(route, 'IDF', this.isIdentityVerified);
      return route;
    },
    onInput(e) {
      this.phone_twilio_validation = e.target.value;
      this.$store.commit('updateSignupField', {
        name: 'phone_twilio_validation',
        value: e.target.value,
      });
    },
    async smallCarrierVerification() {
      // verification code is valid
      // run check for open user invitation
      this.validation_error = false;
      const invitationResult = await this.$store.dispatch(
        'users/patchCheckOpenInvitation'
      );
      if ([200, 201].includes(invitationResult.status)) {
        // invitation exists
        // sent user to page that prompts them to accept/decline invitation
        await this.$store.dispatch('users/refreshCurrentUser');
        // console.log("next page");
        // this.$router.push({ name: "SignUpVerification" });
      } else if (invitationResult.status === 404) {
        // invitation does NOT exist
        // (double check error message here) "no active invitations for this user"
        // Skip ahead to company type
        console.log('#IDV-4');
        const router = this.getNextRoute('CompanyType');
        console.log(router);
        this.$router.push({
          name: router.name,
          params: { canMoveOn: true },
          query: { ...(router.query || {}) },
        });
        // this.$router.push({ name: "SignUpVerification" });
      }
    },
    // Handle a big carrier verification and check if the user
    // is already associated with a company. If they are, send
    // them on to the onboarding packet
    async bigCarrierVerification() {
      console.log('#IDV-5');
      const router = this.getNextRoute('GeneralInformation');
      this.$router.push({
        name: router.name,
        query: { ...(router.query || {}) },
      });
    },
    async userAlreadyAssociatedWithCompany() {
      console.log('User already associated with a company');

      const redirectAfterSignIn = JSON.parse(
        localStorage.getItem('redirect_url_after_sigin')
      );

      const companyType =
        this.$store.state.users.currentUser.company.company_type;
      const fallbackRouteName = (() => {
        switch (companyType) {
          case 'broker':
            return 'Broker Dashboard';
          case 'carrier':
            return 'CarrierWelcomePage';
          case 'dispatcher':
            return 'Dispatcher Dashboard';
          default:
            return 'Home';
        }
      })();

      // Case 1: `redirectAfterSignIn` exists and policies are verified
      if (redirectAfterSignIn && !this.shouldShowPrivacyPolicy) {
        console.log('Redirecting to redirectAfterSignIn, policies verified');
        localStorage.removeItem('redirect_url_after_sigin'); // Remove item after use
        return this.$router.push(redirectAfterSignIn);
      }

      // Case 2: `redirectAfterSignIn` exists but policies are NOT verified
      if (redirectAfterSignIn && this.shouldShowPrivacyPolicy) {
        console.log('Redirecting to PrivacyPolicy for verification');
        return this.$router.push({ name: 'PrivacyPolicy' });
      }

      // Case 3: `redirectAfterSignIn` does NOT exist and policies are NOT verified
      if (!redirectAfterSignIn && this.shouldShowPrivacyPolicy) {
        console.log('Redirecting to PrivacyPolicy with fallback query');
        return this.$router.push({
          name: 'PrivacyPolicy',
          query: { redirectTo: fallbackRouteName }, // Use `fallbackRoute` as `redirectTo`
        });
      }

      // Case 4: `redirectAfterSignIn` does NOT exist and policies are already verified
      console.log('Redirecting to fallback route, policies verified');
      return this.$router.push({ name: fallbackRouteName });
    },
    async handleSubmit() {
      if (this.$route.query.needsClaimCarrier) {
        await this.handleManualVerification();
      } else {
        await this.handleVerification();
      }
    },
    async handleVerificationResponse(res) {
      // Handle an existing user already associated with a company
      console.log(res.data);
      if (res.data['is_trying_to_claim_manual_carrier?']) {
        this.handleManualValidation(res);
        return;
      }
      if (res.data.company?.id != null) {
        return this.userAlreadyAssociatedWithCompany();
      }

      // Check if a large carrier and requires email for sign up
      if (this.isEmailRequiredForLogin) {
        await this.bigCarrierVerification(res);
      }

      // Must be a small carrier signing up
      return await this.smallCarrierVerification(res);
    },
    handleManualValidation(res) {
      const nameIsFilled = res?.data?.first_name?.length > 0;
      const router = this.getNextRoute(
        nameIsFilled ? 'CompanyDetails' : 'YourDetails'
      );

      console.log(nameIsFilled ? '#IDV-2' : '#IDV-1');

      this.$router.push({
        name: router.name,
        query: { ...(router.query || {}) },
      });
    },
    async handleVerification() {
      // check for manual carrier Data to make call to create_manual endpoint
      this.isLoading = true;
      let res = await this.$store.dispatch('users/postSignUpOrLoginCode', {
        code: this.$store.state.signUpData.phone_twilio_validation,
        ...(this.isEmailRequiredForLogin && {
          email_address: this.userEmailAddress,
        }),
        ...(!this.isEmailRequiredForLogin && {
          phone: this.userPhoneNumber,
        }),
      });

      if ([200, 201].includes(res.status)) {
        await Promise.all([
          this.$store.dispatch('users/getFeatureFlags', res.data.id),
          this.$store.dispatch('users/getUserInquiries', res.data.id),
        ]);

        this.handleVerificationResponse(res);
      } else if (res.status === 422) {
        this.validation_error = true;
        toastResponse(this, res, null);
      } else {
        this.validation_error = false;
        toastResponse(this, res, null);
      }
      this.isLoading = false;
    },
    handleUserAlreadyLoggedIn: function () {
      const companyType =
        this.$store.state.users.currentUser.company.company_type;
      switch (companyType) {
        case 'broker':
          return this.$router.push({ name: 'Broker Dashboard' });
        case 'carrier':
          return this.$router.push({ name: 'CarrierWelcomePage' });
        case 'dispatcher':
          return this.$router.push({ name: 'Dispatcher Dashboard' });
        default:
          return this.$router.push({ name: 'Home' });
      }
    },
    async handleManualVerification() {
      this.isLoading = true;
      let res = await this.$store.dispatch(
        'users/postSignUpOrLoginManualCode',
        {
          code: this.$store.state.signUpData.phone_twilio_validation,
        }
      );

      await Promise.all([
        this.$store.dispatch('users/getFeatureFlags', res.data.id),
        this.$store.dispatch('users/getUserInquiries', res.data.id),
      ]);

      this.handleVerificationResponse(res);
      this.isLoading = false;
      const router = this.getNextRoute('YourDetails');
      switch (res.status) {
        case 200:
          this.handleVerificationResponse(res);
          break;
        case 201:
          // carrier already claimed
          this.handleNewCarrierClaimed();
          break;
        case 206:
          console.log('#IDV-6');
          this.$router.push({
            name: router.name,
            query: { ...(router.query || {}) },
          });
          break;
        case 418:
          this.handleUserAlreadyLoggedIn();
          break;
        case 422:
        default:
          this.validation_error = true;
          toastResponse(this, res, null);
      }
    },
    async handleNewCarrierClaimed(carrierId) {
      this.isLoading = true;
      const claimedRes = await this.$store.dispatch(
        'users/hasCarrierBeenClaimed',
        carrierId
      );
      this.isLoading = false;
      if (
        [201, 200].includes(claimedRes.status) ||
        ([304].includes(claimedRes.status) && claimedRes.data?.length > 0)
      ) {
        this.$store.commit('carriers/setMaskedContacts', claimedRes.data);
        this.$router.push({
          name: 'RequestAccessFMSCA',
          params: { carrierHasBeenClaimed: true },
        });
      } else {
        toastResponse(this, claimedRes, null);
      }
    },
  },
};
</script>

<style scoped>
.button {
  @apply my-3 mb-4 bg-light-gray !important;
}
</style>
